import { Controller } from "@hotwired/stimulus"

/**
 * Controller for making task bars movable via the center drag handle.
 * Uses simplified Day + Part grid model (parts 1-5 within each day).
 *
 * Supports two operations:
 * 1. Horizontal repositioning: Move task left/right to change dates (preserves duration)
 * 2. Site move: Drop task on different site row to change site
 *
 * Uses HTML5 drag-and-drop with visual preview via CSS transform.
 */
export default class extends Controller {
  static targets = ["handle"]

  static values = {
    startDay: Number,
    startPart: Number,
    endDay: Number,
    endPart: Number,
  }

  connect() {
    this.partsPerDay = 5
    this.currentPartsChange = 0
    this.lastPartsChange = 0
    this.isDragging = false
  }

  // ==================== DRAG START ====================

  handleDragStart(event) {
    this.isDragging = true
    this.currentPartsChange = 0
    this.lastPartsChange = 0
    // A task worked in several periods draws one bar per period. The drag moves the
    // bar it started on, so its period id travels with the request.
    this.activeBar = event.target.closest('[data-plantafel-resize-target="bar"]')

    // Store initial position from data attributes (Day + Part model)
    this.storeInitialPosition()

    // Calculate grid dimensions
    this.calculateGridDimensions()

    // Store drag start position for calculating delta
    this.dragStartX = event.clientX

    // Set drag data for site moves (used by plantafel_site_drop_controller)
    // Include position data so site drop can calculate duration for repositioning
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData(
      "application/json",
      JSON.stringify({
        taskId: this.element.dataset.taskId,
        currentSiteId: this.element.dataset.siteId,
        taskName: this.element.dataset.taskName || "Task",
        dragType: "task-move",
        // Include position for duration calculation during cross-row drops
        startDay: this.startDay,
        startPart: this.startPart,
        endDay: this.endDay,
        endPart: this.endPart,
      }),
    )

    // Visual feedback
    const bar = this.getBar()
    if (bar) {
      bar.classList.add("ring-2", "ring-bautask-green/50")
      // Store reference for later
      this.barElement = bar
    }

    // Mark body for drop zone detection (used by site_drop_controller)
    document.body.dataset.plantafelTaskDragging = "true"
    // Store task ID so site_drop_controller can find the dragged task element
    document.body.dataset.plantafelDraggedTaskId = this.element.dataset.taskId

    // Highlight valid drop zones (other sites)
    this.highlightDropZones()
  }

  storeInitialPosition() {
    // The bar being dragged knows its own period's position; the row's values are the
    // fallback for a task that draws a single bar.
    const bar = this.activeBar?.dataset
    this.startDay = this.positionOf(
      bar?.segmentStartDay,
      this.hasStartDayValue && this.startDayValue,
      0,
    )
    this.startPart = this.positionOf(
      bar?.segmentStartPart,
      this.hasStartPartValue && this.startPartValue,
      1,
    )
    this.endDay = this.positionOf(bar?.segmentEndDay, this.hasEndDayValue && this.endDayValue, 0)
    this.endPart = this.positionOf(
      bar?.segmentEndPart,
      this.hasEndPartValue && this.endPartValue,
      1,
    )

    // Calculate total position in parts and duration
    this.startTotalParts = this.startDay * this.partsPerDay + this.startPart
    this.endTotalParts = this.endDay * this.partsPerDay + this.endPart
    this.durationInParts = this.endTotalParts - this.startTotalParts
  }

  // Prefers the dragged bar's own value, then the row's, then the default.
  positionOf(fromBar, fromRow, fallback) {
    if (fromBar !== undefined) return Number(fromBar)

    return fromRow === false ? fallback : fromRow
  }

  calculateGridDimensions() {
    // Find the grid container to measure column width
    const bar = this.getBar()
    const container = bar?.closest(".relative")

    if (container) {
      const grid =
        container.querySelector(".grid.pointer-events-none") || container.querySelector(".grid")
      if (grid) {
        const totalWidth = grid.offsetWidth
        const gridCells = grid.querySelectorAll(":scope > div")
        const numColumns = gridCells.length || 7
        this.dayColumnWidth = totalWidth / numColumns
        this.partWidth = this.dayColumnWidth / this.partsPerDay
        return
      }
    }

    // Fallback: estimate based on view mode
    const plantafelContainer = document.querySelector('[data-controller="plantafel"]')
    const viewMode = plantafelContainer?.dataset.plantafelViewModeValue || "week"
    const fallbackContainer = this.element.closest(".relative") || this.element.parentElement
    const containerWidth = fallbackContainer ? fallbackContainer.offsetWidth : 800

    if (viewMode === "3_days") {
      this.dayColumnWidth = containerWidth / 3
    } else {
      this.dayColumnWidth = containerWidth / 7
    }
    this.partWidth = this.dayColumnWidth / this.partsPerDay
  }

  // ==================== DRAG MOVE ====================

  handleDrag(event) {
    // Note: The 'drag' event fires during dragging but clientX/clientY may be 0
    // We'll use dragover on the container instead for position tracking
    // This handler is kept for potential future use
    if (event.clientX === 0 && event.clientY === 0) return

    this.updateVisualPreview(event.clientX)
  }

  updateVisualPreview(currentX) {
    if (!this.isDragging || !this.barElement) return

    const deltaX = currentX - this.dragStartX
    const partsChange = Math.round(deltaX / this.partWidth)

    // Only update if we've crossed a part boundary
    if (partsChange === this.lastPartsChange) return
    this.lastPartsChange = partsChange
    this.currentPartsChange = partsChange

    // Check if new position would be valid (not before day 0)
    const newStartTotal = this.startTotalParts + partsChange
    if (newStartTotal < 1) {
      // Clamp to minimum valid position
      this.currentPartsChange = 1 - this.startTotalParts
    }

    // Update visual preview using transform (doesn't interfere with CSS positioning)
    const snappedDelta = this.currentPartsChange * this.partWidth
    this.barElement.style.transform = `translateX(${snappedDelta}px)`
  }

  // ==================== DRAG END ====================

  handleDragEnd(_event) {
    if (!this.isDragging) return
    this.isDragging = false

    // Remove the ring highlight
    if (this.barElement) {
      this.barElement.classList.remove("ring-2", "ring-bautask-green/50")
    }

    // Remove drop zone highlights and clean up body data
    this.removeDropZoneHighlights()
    this.removeAllPreviewGhosts()
    delete document.body.dataset.plantafelTaskDragging
    delete document.body.dataset.plantafelDraggedTaskId

    // Restore bar opacity (may have been dimmed by site_drop_controller preview)
    if (this.barElement) {
      this.barElement.style.opacity = ""
    }

    // Check if a site drop was handled (flag set by site_drop_controller)
    if (document.body.dataset.plantafelSiteDropHandled) {
      delete document.body.dataset.plantafelSiteDropHandled
      // Reset transform since we're not saving horizontal position
      if (this.barElement) {
        this.barElement.style.transform = ""
      }
      return // Site move was handled, don't reposition
    }

    // If there was a horizontal change, save the repositioned task
    if (this.currentPartsChange !== 0) {
      // Ensure the bar stays at the new position visually until Turbo replaces it
      // Re-apply the transform in case drag events didn't update it properly
      // (HTML5 drag events often have clientX/Y = 0)
      if (this.barElement && this.partWidth) {
        const snappedDelta = this.currentPartsChange * this.partWidth
        this.barElement.style.transform = `translateX(${snappedDelta}px)`
      }
      this.saveRepositionedTask()
    } else {
      // No change - reset transform
      if (this.barElement) {
        this.barElement.style.transform = ""
      }
    }
  }

  // ==================== SAVE REPOSITIONED TASK ====================

  async saveRepositionedTask() {
    const taskId = this.element.dataset.taskId
    const partsChange = this.currentPartsChange

    // Calculate new positions (preserving duration)
    const newStartTotal = this.startTotalParts + partsChange
    const newEndTotal = newStartTotal + this.durationInParts

    // Validate: can't go before day 0, part 1
    if (newStartTotal < 1) {
      console.warn("Cannot reposition task before start of view")
      return
    }

    // Convert total parts back to day + part (1-5)
    const newStartDay = Math.floor((newStartTotal - 1) / this.partsPerDay)
    const newStartPart = ((newStartTotal - 1) % this.partsPerDay) + 1

    const newEndDay = Math.floor((newEndTotal - 1) / this.partsPerDay)
    const newEndPart = ((newEndTotal - 1) % this.partsPerDay) + 1

    // Add loading state
    if (this.barElement) {
      this.barElement.style.opacity = "0.6"
      this.barElement.style.pointerEvents = "none"
    }

    try {
      const csrfToken = document.head
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content")
      const plantafelContainer = document.querySelector('[data-controller="plantafel"]')
      const viewMode = plantafelContainer?.dataset.plantafelViewModeValue || "week"
      const urlParams = new URLSearchParams(window.location.search)
      const viewStartDate = urlParams.get("date") || this.formatDateForApi(new Date())

      const response = await fetch(`/plantafel/tasks/${taskId}/schedule`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          task: {
            start_day: newStartDay,
            start_part: newStartPart,
            end_day: newEndDay,
            end_part: newEndPart,
            timespan_id: this.currentTimespanId,
          },
          view_mode: viewMode,
          view_start_date: viewStartDate,
        }),
      })

      if (response.ok) {
        const contentType = response.headers.get("Content-Type") || ""
        if (contentType.includes("text/vnd.turbo-stream.html")) {
          const html = await response.text()
          Turbo.renderStreamMessage(html)
        }
      } else {
        // The server explains a refusal in a notification stream — a task with several
        // periods cannot be moved as a whole. Without rendering it the bar just snaps
        // back and the user is told nothing.
        const contentType = response.headers.get("Content-Type") || ""
        if (contentType.includes("text/vnd.turbo-stream.html")) {
          Turbo.renderStreamMessage(await response.text())
        }
        this.resetBarState()
      }
    } catch (error) {
      console.error("Error repositioning task:", error)
      this.resetBarState()
    }
  }

  resetBarState() {
    if (this.barElement) {
      this.barElement.style.opacity = ""
      this.barElement.style.pointerEvents = ""
      this.barElement.style.transform = ""
    }
  }

  // ==================== UTILITY METHODS ====================

  getBar() {
    return this.activeBar || this.element.querySelector('[data-plantafel-resize-target="bar"]')
  }

  get currentTimespanId() {
    return this.activeBar?.dataset.timespanId || ""
  }

  formatDateForApi(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  highlightDropZones() {
    const currentSiteId = this.element.dataset.siteId
    const dropZones = document.querySelectorAll("[data-plantafel-site-drop-zone]")

    dropZones.forEach((zone) => {
      const zoneSiteId = zone.dataset.plantafelSiteDropZoneSiteId
      // Only highlight OTHER sites (not the current one) with subtle background
      if (zoneSiteId && zoneSiteId !== currentSiteId) {
        zone.classList.add("bg-teal-50")
      }
    })
  }

  removeDropZoneHighlights() {
    const dropZones = document.querySelectorAll("[data-plantafel-site-drop-zone]")
    dropZones.forEach((zone) => {
      zone.classList.remove("bg-teal-50")
    })
  }

  removeAllPreviewGhosts() {
    // Remove all preview ghost elements from all drop zones
    document.querySelectorAll(".plantafel-drop-preview").forEach((preview) => {
      preview.remove()
    })
  }

  // Prevent click from bubbling to task bar click handler
  preventClick(event) {
    event.preventDefault()
    event.stopPropagation()
  }
}
